import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToastService } from '../../../services/toast.service';
import { LoaderService } from '../../../services/loader.service';
import { SharedModule } from '../../shared.module';
import { finalize, Observable } from 'rxjs';
import { CompanyService } from '../../../services/company.service';
import { TranslocoModule } from '@ngneat/transloco';
import { enumToOptions, SelectOption } from '../../inputs/select/model/select-option';
import { CompanyType } from '../../models/company/company-type.enum';
import { Company } from '../../models/company/company';
import { fileSizeValidator, fileTypeValidator, OIBvalidator, PDVvalidator } from '../../validators';
import { Country } from '../../models/common/country';

@Component({
  selector: 'psc-upsert-company',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SharedModule, TranslocoModule],
  templateUrl: './upsert-company.component.html',
})
export abstract class UpsertCompanyComponent implements OnInit {
  @Input() companyType: CompanyType = CompanyType.BANK;
  @Input() hideCancel: boolean = false;
  @Input() owner: boolean = false;
  @Output() companyUpserted: EventEmitter<Company> = new EventEmitter<Company>();
  @Output() cancel: EventEmitter<void> = new EventEmitter<void>();
  formGroup: FormGroup;
  countriesOptions: SelectOption[] = enumToOptions(Country);
  selectedCountry: Country;

  constructor(
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    protected loaderService: LoaderService,
    protected companyService: CompanyService,
  ) {
  }

  ngOnInit(): void {
    this.formGroup = this.formBuilder.group({
      companyName: ['', Validators.required],
      companyType: [this.companyType],
      address: [''],
      country: [''],
      website: [''],
      tel: [''],
      companyOIB: ['', [Validators.required, OIBvalidator]],
      email: ['', [Validators.email]],
      description: [''],
      logo: ['', [fileTypeValidator(['jpg', 'jpeg', 'png', 'svg']), fileSizeValidator(2048)]],
    });
  }

  setInitialCountry(value: Country) {
    this.selectedCountry = value;
  }

  countryChange(value: Country) {
    this.selectedCountry = value;
    const validators = value === Country.HR ? [Validators.required, OIBvalidator] : [Validators.required, PDVvalidator];
    this.formGroup.get('companyOIB')?.setValue(this.formGroup.get('companyOIB')?.value);
    this.formGroup.get('companyOIB')?.setValidators(validators);
    this.formGroup.get('companyOIB')?.updateValueAndValidity;
  }

  saveCompany() {
    if (!this.formGroup.valid) {
      return;
    }
    this.loaderService.showLoader();
    const value = JSON.parse(JSON.stringify(this.formGroup.value));
    delete value.logo;
    this.saveToApi(value, this.formGroup.controls['logo'].value[0]).pipe(
      finalize(() => {
        this.loaderService.hideLoader();
      }),
    ).subscribe((company) => {
      this.toastService.showSuccessToast();
      this.companyUpserted.emit(company);
    });
  }


  protected abstract saveToApi(company: Company, logo: File): Observable<Company>;


}
